import { getMessages } from "@/i18n/runtime";

export type PaymentCreditsSectionId =
  | "operations"
  | "terms"
  | "privacy"
  | "commerce";

export interface PaymentCreditsPanel {
  readonly root: HTMLDivElement;
  openSection(id: PaymentCreditsSectionId): void;
  dispose(): void;
}

const CREDIT_SECTION_IDS: readonly PaymentCreditsSectionId[] = [
  "operations",
  "terms",
  "privacy",
  "commerce",
] as const;

export function createPaymentCreditsPanel(): PaymentCreditsPanel {
  const messages = getMessages();
  const cleanups: Array<() => void> = [];

  const root = document.createElement("div");
  root.className = "hover-idle-payment-credits";

  const header = document.createElement("div");
  header.className = "hover-idle-payment-credits-header";

  const title = document.createElement("h3");
  title.className = "hover-idle-payment-credits-title";
  title.textContent = messages.payment.credits.title;

  const badge = document.createElement("span");
  badge.className = "hover-idle-payment-credits-badge";
  badge.textContent = messages.payment.credits.badge;

  header.append(title, badge);

  const description = document.createElement("p");
  description.className = "hover-idle-payment-description";
  description.textContent = messages.payment.credits.description;

  const tabBar = document.createElement("div");
  tabBar.className = "hover-idle-payment-credits-tabs";
  tabBar.setAttribute("role", "tablist");
  tabBar.setAttribute("aria-label", messages.payment.credits.sectionsAria);

  const panels = new Map<PaymentCreditsSectionId, HTMLElement>();
  const buttons = new Map<PaymentCreditsSectionId, HTMLButtonElement>();
  let activeSectionId: PaymentCreditsSectionId = "operations";

  const setActiveSection = (id: PaymentCreditsSectionId): void => {
    if (id === activeSectionId) {
      return;
    }

    const previousPanel = panels.get(activeSectionId);
    if (previousPanel instanceof HTMLElement) {
      previousPanel.hidden = true;
    }

    const nextPanel = panels.get(id);
    if (nextPanel instanceof HTMLElement) {
      nextPanel.hidden = false;
    }

    for (const [sectionId, button] of buttons) {
      const isActive = sectionId === id;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-selected", isActive ? "true" : "false");
      button.tabIndex = isActive ? 0 : -1;
    }

    activeSectionId = id;
  };

  for (const id of CREDIT_SECTION_IDS) {
    const sectionMessages = messages.payment.credits.sections[id];

    const button = document.createElement("button");
    button.type = "button";
    button.id = `hover-idle-payment-credits-tab-${id}`;
    button.className = "hover-idle-payment-credits-tab";
    button.textContent = sectionMessages.label;
    button.dataset.creditTab = id;
    button.dataset.active = id === activeSectionId ? "true" : "false";
    button.setAttribute("role", "tab");
    button.setAttribute(
      "aria-selected",
      id === activeSectionId ? "true" : "false",
    );
    button.tabIndex = id === activeSectionId ? 0 : -1;

    const handleClick = (): void => {
      setActiveSection(id);
    };
    button.addEventListener("click", handleClick);
    cleanups.push(() => button.removeEventListener("click", handleClick));

    const panel = createCreditsSection(id);
    panel.id = `hover-idle-payment-credits-panel-${id}`;
    panel.setAttribute("role", "tabpanel");
    panel.setAttribute("aria-labelledby", button.id);
    panel.hidden = id !== activeSectionId;
    button.setAttribute("aria-controls", panel.id);

    buttons.set(id, button);
    panels.set(id, panel);
    tabBar.append(button);
    root.append(panel);
  }

  root.prepend(header, description, tabBar);

  return {
    root,
    openSection(id: PaymentCreditsSectionId): void {
      setActiveSection(id);
    },
    dispose(): void {
      for (const cleanup of cleanups) {
        cleanup();
      }
      root.remove();
    },
  };

  function createCreditsSection(id: PaymentCreditsSectionId): HTMLDivElement {
    const sectionMessages = messages.payment.credits.sections[id];

    const panel = document.createElement("div");
    panel.className = "hover-idle-payment-credits-panel";
    panel.dataset.creditSection = id;

    const title = document.createElement("h4");
    title.className = "hover-idle-payment-credits-section-title";
    title.textContent = sectionMessages.title;

    const lead = document.createElement("p");
    lead.className = "hover-idle-payment-description";
    lead.textContent = sectionMessages.lead;

    const list = document.createElement("ul");
    list.className = "hover-idle-payment-credits-list";
    list.replaceChildren(
      ...sectionMessages.items.map((itemText) => {
        const item = document.createElement("li");
        item.className = "hover-idle-payment-credits-item";
        item.textContent = itemText;
        return item;
      }),
    );

    const note = document.createElement("p");
    note.className = "hover-idle-payment-credits-note";
    note.textContent = sectionMessages.note;

    panel.append(title, lead, list, note);
    return panel;
  }
}
